import {
    Button, Label, Table, TableBody,
    TableCell,
    TableCellActions,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    makeStyles, shorthands
} from "@fluentui/react-components";
import { DeleteFilled, DeleteRegular, EditFilled, EditRegular, bundleIcon } from "@fluentui/react-icons";
import { collection, getDocs, query, where } from "firebase/firestore";
import debounce from 'lodash/debounce';
import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import CustomSearchInput from "./components/CustomSearchInput";
import DeleteEventDialog from "./components/DeleteEventDialog";
import SpaceClosuresForm from "./components/Forms/SpaceClosuresForm";
import Layout from "./components/Layout";
import useBreakpoint from "./hooks/useBreakpoint";
import { auth, db, trackException } from "./services/firebase";
import roomsDataService from "./services/roomsDataService";
import { PlusSVG } from "./static/svgs";
import moment from "moment";
import OperatingHoursForm from "./components/Forms/OperatingHoursForm";
import ExportCsvButtons from "./components/ExportCsvButtons";

const EditIcon = bundleIcon(EditFilled, EditRegular);
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular);

const useStyles = makeStyles({
    root: {
        ...shorthands.overflow("hidden"),
        display: "flex",
        height: "480px",
        backgroundColor: "#fff"
    },

    content: {
        ...shorthands.flex(1),
        ...shorthands.padding("16px"),
        display: "block",
        ...shorthands.gridArea("main")
    },

    breakFlex: {
        flexBasis: "100%",
        textAlign: "left"
    }
});

const columns = [
    { columnKey: "spaceType", label: "Space Type" },
    { columnKey: "closingDate", label: "Closing Date" },
    { columnKey: "startClosingDate", label: "Start Closing Date" },
    { columnKey: "endClosingDate", label: "End Closing Date" },
    { columnKey: "reasonforClosure", label: "Reason for Closure" },
];

function SpaceClosures() {
    const styles = useStyles();
    const [user, loading, error] = useAuthState(auth);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState();
    const [allrooms, setAllRooms] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentuserId, setCurrentuserId] = React.useState('');
    const [closureHours, setClosureHours] = useState([]);
    const [filteredClosureHours, setFilteredClosureHours] = useState([]);
    const breakpoint = useBreakpoint();
    const isMobile = breakpoint === "mobile";
    const isTablet = breakpoint === "tablet";
    const [open, setOpen] = React.useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
    const navigate = useNavigate();
    const openForm = (selectedItem) => {
        setIsFormOpen(true);
        setSelectedRoom(selectedItem);
    }

    const fetchUserNameAndRooms = async () => {
        try {
            if (!!user) {
                const userquery = query(collection(db, "Users"), where("email", "==", user?.email));
                const userdoc = await getDocs(userquery);
                const currentuser = userdoc.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
                setCurrentuserId(currentuser[0].id);

                if (userdoc.docs.length > 0) {
                    const roomsQuery = await roomsDataService.getAllRooms();
                    let roomsArr = roomsQuery.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
                    setAllRooms(roomsArr);

                    const closureHoursQuery = await roomsDataService.getClosureHours();
                    let closureHoursArr = closureHoursQuery.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
                    // Filter for only items with type 'SpaceClosure'
                    let spaceClosures = closureHoursArr.filter(item => item.type === "SpaceClosure");
                    // Update the state with filtered data
                    setClosureHours(spaceClosures);
                    setFilteredClosureHours(spaceClosures);
                } else {
                    console.log('no access');
                }
            }
        } catch (err) {
            trackException(JSON.stringify(err))
            console.error(err);
            alert("An error occured while fetching user data");
        }
    };


    useEffect(() => {
        if (loading) return;
        if (!user) return navigate("/");
        fetchUserNameAndRooms();
    }, [user, loading]);

    const openDeleteForm = (selectedItem) => {
        setIsDeleteDialogOpen(true);
        setSelectedRoom(selectedItem);
        // fetchUserNameAndEvents();
    }

    const DeleteRoom = async () => {
        roomsDataService.deleteClosureHours(selectedRoom.id);
        setIsDeleteDialogOpen(false);
        fetchUserNameAndRooms();
    };

    // Debounce the handleSearch function
    const debouncedSearch = debounce(search => {
        const lowerSearch = search.toLowerCase();
        const filtered = closureHours.filter(event => {
            return (
                event.name.toLowerCase().includes(lowerSearch)
            )
        });
        setFilteredClosureHours(filtered);
    }, 300); // Adjust the debounce delay as needed

    const handleSearch = event => {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };
    const handleClear = () => {
        setSearchTerm('');
        debouncedSearch('');
    };

    // A closure is either a single date or a range, and the rows leave the other
    // cells empty. The export keeps that: filling both would invent a closure
    // that is not in the record.
    const toExportRow = (item) => ({
      "Space Type": item?.roomType ?? "",
      "Closing Date": !item?.dateRangeOn && item?.closingDate
        ? moment(item.closingDate).format('DD MMM YYYY') : "",
      "Start Closing Date": item?.dateRangeOn && item?.startDate
        ? moment(item.startDate).format('Do MMMM YYYY hh mm A') : "",
      "End Closing Date": item?.dateRangeOn && item?.endDate
        ? moment(item.endDate).format('Do MMMM YYYY hh mm A') : "",
      "Reason for Closure": item?.reasonClosure ?? "",
    });

    return (
        <Layout>
            <div className={styles.content}>
                <div className={"d-flex justify-space-between top-content"}>
                    <div className={"d-flex"} style={{ gap: 8 }}>
                        <Button icon={<PlusSVG />} className={"primary-btn"} shape="circular" appearance="primary" onClick={() => openForm(undefined)}>{'NEW Space Closure'.toUpperCase()}</Button>
                        <ExportCsvButtons
                            screenName="Space Closures"
                            getFilteredRows={() => filteredClosureHours}
                            getAllRows={() => closureHours}
                            mapRow={toExportRow}
                        />
                    </div>
                    <CustomSearchInput
                       className="custom-search-input"
                        value={searchTerm}
                        onChange={handleSearch}
                        onClick={handleClear}
                    />
                </div>
                <div className={"table-layout"} style = {{ border : isMobile ? 'none' : undefined}}>
                    <Table aria-label="Table with cell actions">
                        <TableHeader className={"list-row"}>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHeaderCell key={column.columnKey}>
                                        <span className={"listHeaderText"}>{column.label}</span>
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>

<TableBody>
  {filteredClosureHours && filteredClosureHours.length > 0 && filteredClosureHours.map((item) =>
    isMobile ? (
      <div key={item.id} className="closure-hours-card">
        <div className="closure-hours-card-header">
          <div className="closure-hours-card-title">{item.roomType}</div>
          <div className="closure-hours-card-actions">
            <Button
              icon={<EditIcon />}
              className="edit-icon"
              appearance="subtle"
              aria-label="Edit"
              onClick={() => openForm(item)}
            />
            <Button
              icon={<DeleteIcon />}
              className="delete-icon"
              appearance="subtle"
              aria-label="Delete"
              onClick={() => openDeleteForm(item)}
            />
          </div>
        </div>
        {!item.dateRangeOn && (
          <div className="closure-hours-card-row">
            <span className="closure-hours-card-label">Closing Date</span>
            <span className="closure-hours-card-value">
              {moment(item.closingDate).format('DD MMM YYYY')}
            </span>
          </div>
        )}
        {item.dateRangeOn && (
          <>
            <div className="closure-hours-card-row">
              <span className="closure-hours-card-label">Start Date</span>
              <span className="closure-hours-card-value">
                {moment(item.startDate).format('Do MMMM YYYY hh mm A')}
              </span>
            </div>
             <span className="separator"></span>
            <div className="closure-hours-card-row">
              <span className="closure-hours-card-label">End Date</span>
              <span className="closure-hours-card-value">
                {moment(item.endDate).format('Do MMMM YYYY hh mm A')}
              </span>
            </div>
          </>
        )}
         <span className="separator"></span>
        <div className="closure-hours-card-row">
          <span className="closure-hours-card-label">Reason</span>
          <span className="closure-hours-card-value">{item.reasonClosure}</span>
        </div>
      </div>
    ) : (
      <TableRow key={item.id}>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            {item.roomType}
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            {item.dateRangeOn ? <div /> :
              <Label title={moment(item.closingDate).format('DD MMM YYYY')}>
                {moment(item.closingDate).format('DD MMM YYYY')}
              </Label>
            }
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            {item.dateRangeOn ?
              <Label title={moment(item.startDate).format('DD MMM YYYY')}>
                {moment(item.startDate).format('Do MMMM YYYY hh mm A')}
              </Label> :
              <div />
            }
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            {item.dateRangeOn ?
              <Label title={moment(item.endDate).format('DD MMM YYYY')}>
                {moment(item.endDate).format('Do MMMM YYYY hh mm A')}
              </Label> :
              <div />
            }
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            <Label title={item.description}>{item.reasonClosure}</Label>
          </TableCellLayout>
          <TableCellActions>
            <Button
              icon={<EditIcon />}
              appearance="subtle"
              aria-label="Edit"
              onClick={() => openForm(item)}
            />
            <Button
              icon={<DeleteIcon />}
              appearance="subtle"
              aria-label="Delete"
              onClick={() => openDeleteForm(item)}
            />
          </TableCellActions>
        </TableCell>
      </TableRow>
    )
  )}
</TableBody>

                    </Table>
                </div>
            </div>
            <SpaceClosuresForm isOpen={isFormOpen} setIsOpen={(val) => setIsFormOpen(val)} selectedRoom={selectedRoom}
                updateGrid={() => { fetchUserNameAndRooms() }} createdBy={currentuserId} roomType={allrooms}></SpaceClosuresForm>
            <DeleteEventDialog isDeleteDialogOpen={isDeleteDialogOpen}
                setIsDeleteDialogOpen={setIsDeleteDialogOpen} DeleteEvent={DeleteRoom} text={'Space Closure'} />
        </Layout>
    )
}

export default SpaceClosures;